import { DateTime } from 'luxon';
import { formatYmd } from '@utils/dateUtils';

export type FeedDayGroupKind = 'today' | 'yesterday' | 'date';

export interface IFeedDayGroup<TItem> {
  /** `YYYY-MM-DD` in the group's tz — also the React key for the section. */
  dayKey: string;
  kind: FeedDayGroupKind;
  items: TItem[];
}

/** Reads the ISO timestamp a list item should be grouped by. */
export type TimestampAccessor<TItem> = (item: TItem) => string;

/**
 * The calendar day (`YYYY-MM-DD`, in `tz`) that `now` falls on. Shared anchor
 * for both day-section grouping (below) and the "today only" quick filter on
 * the card, so the two features can never disagree about where midnight falls.
 */
export function todayKey(tz: string, now: Date): string {
  return formatYmd(DateTime.fromJSDate(now), tz);
}

/** `today` is the pre-computed day-key (`todayKey(tz, now)`) rather than a raw
 *  Date, so a caller that ticks a clock every N seconds can depend on the STABLE
 *  day string and only recompute when midnight actually rolls over. */
export function isTimestampToday(iso: string, tz: string, today: string): boolean {
  return formatYmd(iso, tz) === today;
}

/**
 * Groups list items into a FIXED window of calendar-day sections — today back
 * through `windowDays` days (the alerts' 7-day retention) — anchored to `tz`.
 * Every day in the window gets a section even when it holds nothing, so the
 * reader always sees "TODAY" (possibly empty) rather than the list silently
 * starting at yesterday. Sections are date-descending: today, yesterday, then
 * each older day.
 *
 * Generic over the item type (and its timestamp field) because the dashboard
 * renders more than one kind of dated list.
 *
 * An item OLDER than the window (possible right at the retention boundary,
 * before the TTL reaper catches it) still gets a trailing section rather than
 * being dropped — data is never hidden by the scaffold.
 */
export function groupItemsByDay<TItem>(
  items: TItem[],
  getTimestamp: TimestampAccessor<TItem>,
  tz: string,
  todayDayKey: string,
  windowDays = 7,
): IFeedDayGroup<TItem>[] {
  const today = DateTime.fromISO(todayDayKey, { zone: tz });

  const groups: IFeedDayGroup<TItem>[] = [];
  const byKey = new Map<string, IFeedDayGroup<TItem>>();
  for (let offset = 0; offset < windowDays; offset += 1) {
    const dayKey = today.minus({ days: offset }).toFormat('yyyy-MM-dd');
    const kind: FeedDayGroupKind = offset === 0 ? 'today' : offset === 1 ? 'yesterday' : 'date';
    const group: IFeedDayGroup<TItem> = { dayKey, kind, items: [] };
    byKey.set(dayKey, group);
    groups.push(group);
  }

  for (const item of items) {
    const dayKey = formatYmd(getTimestamp(item), tz);
    let group = byKey.get(dayKey);
    if (!group) {
      // Outside the scaffold (older than the window, or a clock-skewed
      // "tomorrow"): keep the item in its own trailing section.
      group = { dayKey, kind: 'date', items: [] };
      byKey.set(dayKey, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}
